import React, { useState } from 'react';
import PropTypes from 'prop-types';
import InvoiceDetailsPopup from './InvoiceDetailsPopup';
import WhatsAppMessagePreview from './WhatsAppMessagePreview';
import { usePdfDownload } from './Hooks/usePdfDownload';
import PdfDownloadModal from './Subcomponents/PdfDownloadModal';
import {
    useInvoiceListData,
    useInvoiceDetailsPopup,
    useWhatsAppPopup,
    useInvoiceActions,
} from './Hooks/InvoicesList.hooks';
import {
    InvoiceTable,
    InvoiceCounter,
    InvoicePagination,
} from './Subcomponents/InvoicesList.subcomponents';
import {
    MOCK_INVOICES,
} from './utils/InvoicesList.utils';


// ============================================================================
// Main Component - InvoicesList
// ============================================================================

const InvoicesList = ({
    filterData,
    setfilterData,
    apiData = null,
    totalRecords = 0,
    isAdmin = true,
    isLoading = false,
}) => {
    // State for PDF download
    const [downloadingInvoiceId, setDownloadingInvoiceId] = useState(null);
    const { isLoading: isPdfLoading, downloadInvoicePDF } = usePdfDownload();

    // Use custom hooks
    const { invoiceListData } = useInvoiceListData(apiData || MOCK_INVOICES);
    const { showInvoiceDetailsPopup, selectedInvoice, handleViewInvoice, handleCloseInvoicePopup } = useInvoiceDetailsPopup();
    const { showWhatsAppPopup, selectedInvoiceForWhatsApp, handleWhatsApp, closeWhatsAppPopup, handleOpenWhatsApp } = useWhatsAppPopup();
    const { handleTableAction: baseHandleTableAction, handleSortingColumn, handleDownloadPDF, handleResendEmail } = useInvoiceActions(filterData, setfilterData);

    // Wrapper to pass handlers to handleTableAction
    const handleTableAction = (invoice, actionType) => {
        baseHandleTableAction(invoice, actionType, { handleViewInvoice, handleWhatsApp });
    };

    // Handle PDF download button click
    const handlePdfDownload = async invoice => {
        setDownloadingInvoiceId(invoice.id);
        try {
            await downloadInvoicePDF(invoice);
            // Success: PDF generated and downloaded
        } catch (err) {
            console.error('PDF download failed:', err);
        } finally {
            setDownloadingInvoiceId(null);
        }
    };

    return (
        <>
            <PdfDownloadModal show={isPdfLoading} />
            <InvoiceCounter invoiceListData={invoiceListData} filterData={filterData} setfilterData={setfilterData} />
            <div className="main_content_table_history">
                <div className="tableFixHead incomingTbl">
                    <InvoiceTable
                        invoiceListData={invoiceListData}
                        filterData={filterData}
                        onRowAction={handleTableAction}
                        onSortClick={handleSortingColumn}
                        onDownloadPDF={handlePdfDownload}
                        downloadingInvoiceId={downloadingInvoiceId}
                        isLoading={isLoading}
                    />
                </div>
            </div>
            <InvoicePagination
                invoiceListData={invoiceListData}
                filterData={filterData}
                setfilterData={setfilterData}
                totalRecords={totalRecords}
            />
            {selectedInvoice && (
                <InvoiceDetailsPopup
                    showPopup={showInvoiceDetailsPopup}
                    hidePopup={handleCloseInvoicePopup}
                    invoiceRecord={selectedInvoice}
                    onDownloadPDF={handleDownloadPDF}
                    onResendEmail={handleResendEmail}
                    onWhatsApp={handleWhatsApp}
                />
            )}
            {selectedInvoiceForWhatsApp && (
                <WhatsAppMessagePreview
                    show={showWhatsAppPopup}
                    invoice={selectedInvoiceForWhatsApp}
                    onClose={closeWhatsAppPopup}
                    onOpen={handleOpenWhatsApp}
                />
            )}
        </>
    );
};

// Export transformation functions for testing and external use
export { transformAPIResponseToInvoiceFormat, transformInvoicesArray, MOCK_INVOICES } from './utils/InvoicesList.utils';

// PropTypes validation
InvoicesList.propTypes = {
    filterData: PropTypes.object.isRequired,
    setfilterData: PropTypes.func.isRequired,
    apiData: PropTypes.array,
    totalRecords: PropTypes.number,
    isAdmin: PropTypes.bool,
    isLoading: PropTypes.bool,
};

export default InvoicesList;
